import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, concat } from 'rxjs';
import { ApiService } from '../api/api.service';
import { PermissionsService } from '../permissions/permissions.service';
import moment from 'moment-timezone';
import { map, tap } from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class RequestsService {
  private requestsCache = new Map<string, { data: any, timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  org_uid = localStorage.getItem('org_uid');

  path = "check/exits/organisation/"

  statusMap = {
    'Invited': 0,
    'Declined': 2,
    'In progress': 4,
    'Submitted': 5,
    'Verified': 6,
    'Expired': 3
  }

  trustStatusMap = {
    'Sending': 'Trust Sent',
    'In progress': 'Trust In Progress',
    'Completed': 'Trust Completed',
    'Failed': 'Trust Failed',
    'Expired': 'Trust Expired',
    'Cancelled':  'Trust Cancelled',
    'Declined': 'Trust Declined'
  }

  constructor(
    private apiService: ApiService,
    private permissionsService: PermissionsService
  ) { }

  getAllRequests(): Observable<any> {
    const path = this.path+this.org_uid+'/request/all';
    return this.apiService.get(path);
  }

  getRequests(searchOptions?:any, rowsPerPage=0, pageNumber=1, sortString?: string, filterOptions?:any): Observable<any>{
    let params = new HttpParams();
    if (rowsPerPage){
      params = params.set('limit', String(rowsPerPage));
      params = params.set('offset', String((pageNumber-1)*rowsPerPage))
    }
    if (searchOptions) {
      searchOptions['columns'].forEach(element => {
        params = params.set(element, searchOptions['value'])
      });
    }
    if (sortString){
      params = params.set('sort',sortString);
    }
    if (filterOptions) {
      if(filterOptions['filter'] != 'All'){
        let utcStart = moment(filterOptions['start_date'], "YYYY-MM-DD").utc();
        let utcEnd = moment(filterOptions['end_date'], "YYYY-MM-DD").utc();
        params = params.set('filter', filterOptions['filterBy'].toLowerCase().replace(' ', '_'))
                  .set('start_date', utcStart.format())
                  .set('end_date', utcEnd.format());
      }
      if(filterOptions['checkboxOption'].length){
        params = params.set('status', filterOptions['checkboxOption'].map(status=>this.statusMap[status]).join(','));
      }
      params = params.set('requested_by', filterOptions['radioOption']);
    }
    const path = this.path + 'request/all';
    return this.apiService.get(path, {params});
  }
  getRequestsDuplicateParams(
    searchOptions?: any,
    rowsPerPage = 0,
    pageNumber = 1,
    sortString?: string,
    filterOptions?: any
  ): Observable<any> {
    const cacheKey = this.createCacheKey(searchOptions, rowsPerPage, pageNumber, sortString, filterOptions);

    let params = new HttpParams();
    if (rowsPerPage) {
      params = params.set('limit', String(rowsPerPage));
      params = params.set('offset', String((pageNumber - 1) * rowsPerPage));
    }
    if (searchOptions) {
      searchOptions.forEach(element => {
        params = params.append(element.key, element.value);
      });
    }
    if (sortString) {
      params = params.set('sort', sortString);
    }
    if (filterOptions) {
      if (filterOptions['filter'] != 'All') {
        let utcStart = moment(filterOptions['start_date'], "YYYY-MM-DD").utc();
        let utcEnd = moment(filterOptions['end_date'], "YYYY-MM-DD").utc();
        params = params.set('filter', filterOptions['filterBy'].toLowerCase().replace(' ', '_'))
          .set('start_date', utcStart.format())
          .set('end_date', utcEnd.format());
      }
      if (filterOptions['statusOption'].length) {
        params = params.set(
          'status',
          filterOptions['statusOption'].map(status => this.statusMap[status]).join(',')
        );
      }
      // Handle both unread_only and requested_by independently
      if (Array.isArray(filterOptions['checkboxOption'])) {
        if (filterOptions['checkboxOption'].includes('unread_only')) {
          params = params.set('unread_only', 'true');
        }
        const requestedBy = filterOptions['checkboxOption'].find(opt => opt !== 'unread_only');
        if (requestedBy) {
          params = params.set('requested_by', requestedBy);
        }
      }
    }
    const path = this.path + 'request/all';
    
    // Create the API call observable
    const apiCall$ = this.apiService.get(path, { params }).pipe(
      tap(data => {
        this.requestsCache.set(cacheKey, {
          data: data,
          timestamp: Date.now()
        });
      })
    );

    // Stale-while-revalidate: return cached data first, then fresh data
    if (this.requestsCache.has(cacheKey)) {
      const cached = this.requestsCache.get(cacheKey);
      if (cached) {
        return concat(of(cached.data), apiCall$);
      }
    }

    // No cache - just make API call
    return apiCall$;
  }

  clearCache() {
    this.requestsCache.clear();
  }

  private createCacheKey(search: any, rows: number, page: number, sort: string | undefined, filter: any): string {
    return JSON.stringify({ search, rows, page, sort, filter });
  }

  deprecatedGetRequestsDuplicateParams(searchOptions?:any, rowsPerPage=0, pageNumber=1, sortString?: string, filterOptions?:any): Observable<any>{
    let params = new HttpParams();
    if (rowsPerPage){
      params = params.set('limit', String(rowsPerPage));
      params = params.set('offset', String((pageNumber-1)*rowsPerPage))
    }
    if (searchOptions) {
      searchOptions.forEach(element => {
        params = params.append(element.key, element.value);
      });
    }
    if (sortString){
      params = params.set('sort',sortString);
    }
    if (filterOptions) {
      if(filterOptions['filter'] != 'All'){
        let utcStart = moment(filterOptions['start_date'], "YYYY-MM-DD").utc();
        let utcEnd = moment(filterOptions['end_date'], "YYYY-MM-DD").utc();
        params = params.set('filter', filterOptions['filterBy'].toLowerCase().replace(' ', '_'))
                  .set('start_date', utcStart.format())
                  .set('end_date', utcEnd.format());
      }
      if(filterOptions['statusOption'].length){
        params = params.set('status', filterOptions['statusOption'].map(status=>this.statusMap[status]).join(','));
      }
      params = params.set('requested_by', filterOptions['checkboxOption'][0]);
    }
    const path = this.path + 'request/all';
    return this.apiService.get(path, {params});
  }
  setSearchOptionsParams(params, searchOptions?:any){
    if (searchOptions) {
      searchOptions.forEach(element => {
        // filtering params
        if(element.key == 'check' || element.key == 'all'){
          // filtering param to check MobileID in check and all tags
          if(element.value.startsWith('MobileID - ') && element.value!='MobileID - '){
            element.value = element.value.replace('MobileID - ', '')
          }
          else if(element.value == 'MobileID' || element.value == 'MobileID - '){
            element.value = 'ID'
          }
        }
        params = params.append(element.key, element.value);
      });
    }
    return params
  }
  getChecksRequestsParams(searchOptions?:any, rowsPerPage=0, pageNumber=1, sortString?: string, filterOptions?:any): Observable<any>{
    let params = new HttpParams();
    if (rowsPerPage){
      params = params.set('limit', String(rowsPerPage));
      params = params.set('offset', String((pageNumber-1)*rowsPerPage))
    }
    params = this.setSearchOptionsParams(params, searchOptions)
    if (sortString){
      params = params.set('ordering',sortString);
    }
    if (filterOptions) {
      if(filterOptions['filter'] != 'All'){
        let utcStart = moment(filterOptions['start_date'], "YYYY-MM-DD").utc();
        let utcEnd = moment(filterOptions['end_date'], "YYYY-MM-DD").utc();
        params = params.set('filter', filterOptions['filterBy'].toLowerCase().replace(' ', '_'))
                  .set('start_date', utcStart.format())
                  .set('end_date', utcEnd.format());
      }
      if(filterOptions['statusOption'].length){
        params = params.set('state', filterOptions['statusOption'].join(','));
      }
      params = params.set('requested_by', filterOptions['checkboxOption'][0]);
    }
    const path = 'check/external_checks/external-check-request'
    return this.apiService.get(path, {params});
  }

  deleteRequest(requestId){
    const path = "checks/exit/check/" + requestId;
    return this.apiService.delete(path);
  }
}
